import { PlayerInput } from '../shared/types';
import { MAX_INPUT_SEQ } from '../shared/constants';
import { clamp } from '../shared/utils';

export class InputManager {
  public yaw = 0;
  public pitch = 0;
  public seq = 0;
  public locked = false;
  public mouseDeltaX = 0;
  public mouseDeltaY = 0;
  public mouseSensitivity = 0.0026;
  public adsMultiplier = 0.4;
  public invertY = false;

  private keys = new Map<string, boolean>();
  private keysJustPressed = new Map<string, boolean>();
  private mouseButtons = new Map<number, boolean>();
  private mouseButtonsJustPressed = new Map<number, boolean>();
  private canvas: HTMLCanvasElement;
  private rawMouseX = 0;
  private rawMouseY = 0;
  private pendingDeltaX = 0;
  private pendingDeltaY = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.bindEvents();
  }

  private bindEvents(): void {
    window.addEventListener('keydown', this.onKeyDown.bind(this));
    window.addEventListener('keyup', this.onKeyUp.bind(this));
    window.addEventListener('mousedown', this.onMouseDown.bind(this));
    window.addEventListener('mouseup', this.onMouseUp.bind(this));
    window.addEventListener('mousemove', this.onMouseMove.bind(this));
    window.addEventListener('wheel', this.onWheel.bind(this));
    window.addEventListener('contextmenu', (e) => e.preventDefault());

    document.addEventListener('pointerlockchange', () => {
      this.locked = document.pointerLockElement === this.canvas;
    });
  }

  private onKeyDown(e: KeyboardEvent): void {
    if (!this.keys.get(e.code)) {
      this.keysJustPressed.set(e.code, true);
    }
    this.keys.set(e.code, true);
  }

  private onKeyUp(e: KeyboardEvent): void {
    this.keys.set(e.code, false);
  }

  private onMouseDown(e: MouseEvent): void {
    if (!this.mouseButtons.get(e.button)) {
      this.mouseButtonsJustPressed.set(e.button, true);
    }
    this.mouseButtons.set(e.button, true);
  }

  private onMouseUp(e: MouseEvent): void {
    this.mouseButtons.set(e.button, false);
  }

  private onMouseMove(e: MouseEvent): void {
    if (!this.locked) return;
    this.pendingDeltaX += e.movementX;
    this.pendingDeltaY += e.movementY;
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
  }

  requestLock(): void {
    this.canvas.requestPointerLock();
  }

  exitLock(): void {
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }
  }

  isKey(code: string): boolean {
    return this.keys.get(code) === true;
  }

  wasKeyJustPressed(code: string): boolean {
    return this.keysJustPressed.get(code) === true;
  }

  isMouse(button: number): boolean {
    return this.mouseButtons.get(button) === true;
  }

  wasMouseJustPressed(button: number): boolean {
    return this.mouseButtonsJustPressed.get(button) === true;
  }

  consumeMouseDelta(): { dx: number; dy: number } {
    const dx = this.pendingDeltaX;
    const dy = this.pendingDeltaY;
    this.pendingDeltaX = 0;
    this.pendingDeltaY = 0;
    return { dx, dy };
  }

  applyMouseLook(invertY: boolean = false): void {
    const { dx, dy } = this.consumeMouseDelta();
    this.yaw -= dx * this.mouseSensitivity;
    this.pitch -= dy * this.mouseSensitivity * (invertY ? -1 : 1);
    this.pitch = clamp(this.pitch, -Math.PI / 2 + 0.01, Math.PI / 2 - 0.01);
    this.mouseDeltaX = dx;
    this.mouseDeltaY = dy;
  }

  collectInput(weaponSlot: import('../shared/types').WeaponSlot = 'primary'): PlayerInput {
    const sprint = this.isKey('ShiftLeft') || this.isKey('ShiftRight');
    const input: PlayerInput = {
      seq: ++this.seq > MAX_INPUT_SEQ ? (this.seq = 1) : this.seq,
      forward: this.isKey('KeyW') || this.isKey('ArrowUp'),
      back: this.isKey('KeyS') || this.isKey('ArrowDown'),
      left: this.isKey('KeyA') || this.isKey('ArrowLeft'),
      right: this.isKey('KeyD') || this.isKey('ArrowRight'),
      jump: this.isKey('Space'),
      crouch: this.isKey('ControlLeft') || this.isKey('ControlRight'),
      sprint,
      slide: this.wasKeyJustPressed('ControlLeft') || this.wasKeyJustPressed('ControlRight'),
      fire: this.isMouse(0),
      aim: this.isMouse(2),
      reload: this.wasKeyJustPressed('KeyR'),
      weaponSlot,
      yaw: this.yaw,
      pitch: this.pitch,
      mouseDeltaX: this.mouseDeltaX,
      mouseDeltaY: this.mouseDeltaY,
      timestamp: performance.now(),
    };
    return input;
  }

  endFrame(): void {
    this.keysJustPressed.clear();
    this.mouseButtonsJustPressed.clear();
    this.mouseDeltaX = 0;
    this.mouseDeltaY = 0;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown.bind(this));
    window.removeEventListener('keyup', this.onKeyUp.bind(this));
    window.removeEventListener('mousedown', this.onMouseDown.bind(this));
    window.removeEventListener('mouseup', this.onMouseUp.bind(this));
    window.removeEventListener('mousemove', this.onMouseMove.bind(this));
  }
}
